import {
  Audience,
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterType,
} from '../../audience/components/audience.model';
import { isPureInternal } from './audience-source.utils';
import { isNegationOperator } from './reach-estimate-factors.utils';

/**
 * Guesstimate math for mixed audiences (internal rules + 3rd-party list filter).
 *
 * The platform exposes the *list total* via `/integrations/reach/{app}/{listId}`
 * for the apps listed in `REACH_SUPPORTED_APPS`. For everyone else we don't
 * know the list total, so we can't guesstimate — those audiences keep showing
 * N/A.
 *
 * For a *mixedSupported* audience we combine:
 *   - L: 3rd-party list total
 *   - I: internal-only count (counted via DataPlatform with the 3rd-party
 *        filter stripped — the legacy mapper already drops AppList et al.)
 *   - T: total identified visitors over the same period
 *
 *   ratio  = I / T                  // share of identified visitors that
 *                                   //   match our internal rules
 *   centre = L × ratio              // most-likely estimate
 *   upper  = min(L, I)              // can't exceed either side
 *   lower  = max(0, centre × 0.6)   // ±40% spread, asymmetric — centre is
 *                                   //   more reliable than the floor
 *
 * The math is pure JS. The data inputs come from existing services
 * (no new backend work).
 */

/** Apps whose list total is exposed via `getIntegrationsReach`. Keep in
 *  sync with the backend's reach-implementing integrations. */
export const REACH_SUPPORTED_APPS: readonly AudienceFilterApp[] = [
  AudienceFilterApp.Brevo,
  AudienceFilterApp.CleverReach,
  AudienceFilterApp.Custobar,
  AudienceFilterApp.Dotdigital,
  AudienceFilterApp.Emarsys,
  AudienceFilterApp.Klaviyo,
  AudienceFilterApp.Mailchimp,
  AudienceFilterApp.MailerLite,
  AudienceFilterApp.Rule,
  AudienceFilterApp.Shopware,
];

/** Apps whose reach endpoint sizes an AppSegment id. The others look the id up as a list. */
export const SEGMENT_REACH_SUPPORTED_APPS: readonly AudienceFilterApp[] = [AudienceFilterApp.CleverReach];

export function canSizeThirdPartyFilter(filter: AudienceFilter): boolean {
  if (!filter.app || !REACH_SUPPORTED_APPS.includes(filter.app)) {
    return false;
  }
  if (filter.type === AudienceFilterType.AppList) {
    return true;
  }
  return filter.type === AudienceFilterType.AppSegment && SEGMENT_REACH_SUPPORTED_APPS.includes(filter.app);
}

const LOWER_BOUND_SHARE = 0.6;

export type AudienceReachCategory =
  | 'internal' // only internal filters — use the existing exact reach
  | 'thirdPartyOnly' // a single 3rd-party AppList filter on a supported app
  | 'mixedSupported' // internal + 3rd-party on a supported app → guesstimate
  | 'mixedUnsupported' // anything else with a 3rd-party app we can't size → N/A
  | 'unsupported'; // legacy reach helper rejects it for some other reason

export interface ThirdPartySource {
  app: AudienceFilterApp;
  listId: string;
  negated: boolean;
}

export interface GuesstimateInput {
  /** L — the 3rd-party list total. */
  listTotal: number;
  /** I — the internal-only count (after the 3rd-party filter is stripped). */
  internalCount: number;
  /** T — total identified visitors over the same period. */
  totalIdentified: number;
}

export interface GuesstimateResult {
  centre: number;
  lower: number;
  upper: number;
}

/**
 * Picks the bucket an audience falls into for reach display purposes.
 */
export function categoriseAudience(audience: Audience | null | undefined): AudienceReachCategory {
  if (!audience) return 'unsupported';
  const filters = audience.filters ?? [];

  if (filters.length === 0 || isPureInternal(audience)) {
    return 'internal';
  }

  const thirdPartyFilters = filters.filter((f) => !!f.app);
  if (thirdPartyFilters.length === 0) {
    return 'internal';
  }

  // Multiple distinct 3rd-party apps → we can't size that today.
  const distinctApps = new Set(thirdPartyFilters.map((f) => f.app!));
  if (distinctApps.size > 1) {
    return 'mixedUnsupported';
  }

  if (!thirdPartyFilters.every(canSizeThirdPartyFilter)) {
    return 'mixedUnsupported';
  }

  // Pure 3rd-party (no internal filter) on a supported app.
  const internalFilters = filters.filter((f) => !f.app);
  if (internalFilters.length === 0) {
    return 'thirdPartyOnly';
  }

  return 'mixedSupported';
}

/**
 * Returns the (app, listId) the audience uses, or null if there isn't exactly one.
 * The legacy reach endpoint takes a single listId per audience, so audiences with
 * two AppList filters of the same app don't have a unique identifier here.
 */
export function extractThirdPartySource(audience: Audience): ThirdPartySource | null {
  const filters = audience.filters ?? [];
  const candidates: ThirdPartySource[] = [];
  for (const filter of filters) {
    if (!filter.app) continue;
    const listId = extractListId(filter);
    if (listId) candidates.push({ app: filter.app, listId, negated: isNegationOperator(filter.operator) });
  }
  if (candidates.length === 0) return null;
  // Multiple distinct lists → can't disambiguate. Caller should treat as unsupported.
  const distinct = new Set(candidates.map((c) => `${c.app}:${c.listId}:${c.negated}`));
  if (distinct.size > 1) return null;
  return candidates[0];
}

/**
 * Computes the guesstimate triplet (centre, lower, upper). All inputs are
 * expected to be non-negative; negative inputs are clamped to 0. Edge cases:
 *  - If `totalIdentified` is 0 → ratio undefined → centre falls back to
 *    `internalCount` (best we can say is "you have this many that match
 *    internally"; the 3rd-party list adds nothing without a denominator).
 *  - centre and upper are always rounded to whole profiles (we don't deal in
 *    fractions of a person).
 */
export function computeGuesstimate(input: GuesstimateInput): GuesstimateResult {
  const L = Math.max(0, input.listTotal);
  const I = Math.max(0, input.internalCount);
  const T = Math.max(0, input.totalIdentified);

  if (T === 0) {
    const fallback = Math.round(I);
    return { centre: fallback, lower: fallback, upper: fallback };
  }

  const ratio = I / T;
  const rawCentre = L * ratio;
  const rawUpper = Math.min(L, I);
  const rawLower = Math.max(0, rawCentre * LOWER_BOUND_SHARE);

  // Clamp centre to upper bound — math is allowed to drift past it for
  // very high ratios.
  const centre = Math.round(Math.min(rawCentre, rawUpper));
  const upper = Math.round(rawUpper);
  const lower = Math.round(Math.min(rawLower, centre));

  return { centre, lower, upper };
}

/**
 * With no identified visitors nobody is on the list, so the whole base stays.
 * The overlap's floor is taken from its clamped centre: computeGuesstimate's lower
 * collapses onto the centre when the list outnumbers the identified profiles.
 */
export function computeNotInListGuesstimate(base: number, input: GuesstimateInput): GuesstimateResult {
  const total = Math.max(0, Math.round(base));
  const overlap = input.totalIdentified > 0 ? computeGuesstimate(input) : { centre: 0, upper: 0 };
  return {
    centre: Math.max(0, total - overlap.centre),
    lower: Math.max(0, total - overlap.upper),
    upper: Math.max(0, total - Math.round(overlap.centre * LOWER_BOUND_SHARE)),
  };
}

// ---- internals ----

function extractListId(filter: AudienceFilter): string | null {
  if (filter.value == null) return null;
  if (typeof filter.value === 'string') return filter.value;
  if (typeof filter.value === 'object') {
    // Some integrations (e.g. Custobar AppTags) encode value as JSON with a TagId field.
    const tagId = (filter.value as { TagId?: unknown }).TagId;
    if (typeof tagId === 'string') return tagId;
  }
  return null;
}
